/**
 * ICF Compliance Demo — pre-baked seed data.
 *
 * Everything AudRI "analyzes" on the ICF screens comes from this file.
 * No live LLM calls. All content is authoritative to the spec.
 */

export const TRIAL_CONTEXT = {
    trial_id: "ONCO-247",
    phase: "Phase II",
    indication: "Advanced Non-Small Cell Lung Cancer (NSCLC)",
    sponsor: "Meridian Oncology",
    sites: "12 (US)",
    participants: 87,
    protocol_version: "v2.1 · approved 2025-11-14",
    pending_amendment: "v2.2 · pharmacogenomic biomarker sub-study",
    master_icf: "v3.1",
    irb: "Advarra Central IRB",
};

// ---------- EXTRACT screen ----------

export const SOURCES = [
    {
        id: "src-protocol",
        title: "Protocol Amendment v2.2",
        subtitle: "PDF · Meridian Oncology",
        icon: "FileText",
        status: "Loaded",
    },
    {
        id: "src-cfr",
        title: "21 CFR Part 50 — Subpart B",
        subtitle: "§ 50.25 Elements of informed consent",
        icon: "BookOpen",
        status: "Loaded",
    },
    {
        id: "src-ich",
        title: "ICH-GCP E6(R3) — Section 4.8",
        subtitle: "Informed consent of trial subjects",
        icon: "BookOpen",
        status: "Loaded",
    },
];

export const RULES = [
    {
        id: "RULE-050.25-A-1",
        name: "Statement that the study involves research",
        citation: "21 CFR 50.25(a)(1)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must contain a statement that the study involves research, an explanation of the purposes of the research, the expected duration of participation, a description of procedures, and identification of any experimental procedures.",
        exact:
            "A statement that the study involves research, an explanation of the purposes of the research and the expected duration of the subject's participation, a description of the procedures to be followed, and identification of any procedures which are experimental.",
    },
    {
        id: "RULE-050.25-A-2",
        name: "Reasonably foreseeable risks or discomforts",
        citation: "21 CFR 50.25(a)(2)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must describe reasonably foreseeable risks or discomforts to the subject.",
        exact:
            "A description of any reasonably foreseeable risks or discomforts to the subject.",
    },
    {
        id: "RULE-050.25-A-3",
        name: "Description of benefits",
        citation: "21 CFR 50.25(a)(3)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must describe benefits to the subject or to others that may reasonably be expected from the research.",
    },
    {
        id: "RULE-050.25-A-4",
        name: "Disclosure of alternative procedures",
        citation: "21 CFR 50.25(a)(4)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must disclose appropriate alternative procedures or courses of treatment, if any, that might be advantageous to the subject.",
    },
    {
        id: "RULE-050.25-A-5",
        name: "Confidentiality of records",
        citation: "21 CFR 50.25(a)(5)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must include a statement describing the extent to which confidentiality of records identifying the subject will be maintained, and notes the possibility that FDA may inspect the records.",
    },
    {
        id: "RULE-050.25-A-6",
        name: "Compensation for injury",
        citation: "21 CFR 50.25(a)(6)",
        type: "Required",
        source: "Regulatory",
        description:
            "For research involving more than minimal risk, an explanation as to whether any compensation and any medical treatments are available if injury occurs.",
    },
    {
        id: "RULE-050.25-A-7",
        name: "Contacts for questions and injury",
        citation: "21 CFR 50.25(a)(7)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must provide an explanation of whom to contact for answers to pertinent questions about the research and research subjects' rights, and whom to contact in the event of a research-related injury.",
    },
    {
        id: "RULE-050.25-A-8",
        name: "Voluntary participation and right to withdraw",
        citation: "21 CFR 50.25(a)(8)",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF must include a statement that participation is voluntary, refusal to participate will involve no penalty or loss of benefits to which the subject is otherwise entitled, and the subject may discontinue participation at any time without penalty.",
    },
    {
        id: "RULE-050.25-B-5",
        name: "Future biospecimen use disclosure",
        citation: "21 CFR 50.25(b)(5)",
        type: "Conditional",
        source: "Regulatory",
        description:
            "When research involves collection of biospecimens intended for future research use, the ICF must disclose that identifiable specimens will (or will not) be used or distributed for future research.",
        trigger:
            "Amendment v2.2 introduces optional biospecimen storage for future pharmacogenomic research.",
        badge: "NEW",
        introduced_by: "Amendment v2.2",
    },
    {
        id: "RULE-ICH-4.8.10",
        name: "Revise ICF when important new information arises",
        citation: "ICH-GCP E6(R3) §4.8.10",
        type: "Required",
        source: "Regulatory",
        description:
            "The ICF and any other written information provided to subjects must be revised whenever important new information becomes available that may be relevant to the subject's consent.",
    },
    {
        id: "RULE-PROT-247-4.7",
        name: "Describe pharmacogenomic biomarker sub-study",
        citation: "Protocol Amendment v2.2 §4.7",
        type: "Required",
        source: "Protocol-derived",
        description:
            "The ICF must describe the pharmacogenomic biomarker sub-study procedures, including the additional peripheral blood draw of 10 mL at study visits 3, 5, and 7.",
        badge: "NEW",
    },
    {
        id: "RULE-PROT-247-6.2",
        name: "Additional venipuncture risks",
        citation: "Protocol Amendment v2.2 §6.2",
        type: "Required",
        source: "Protocol-derived",
        description:
            "The ICF risks section must include minor risks associated with additional venipuncture: bruising, hematoma, and low risk of infection at the draw site.",
        badge: "MODIFIED",
    },
];

export const RULE_DIFF = {
    new_count: 4,
    modified_count: 2,
    unchanged_count: 18,
    entries: [
        { id: "RULE-050.25-B-5", state: "NEW", title: "Future biospecimen use disclosure" },
        { id: "RULE-PROT-247-4.7", state: "NEW", title: "Biomarker sub-study procedures" },
        { id: "RULE-PROT-247-8.3", state: "NEW", title: "Biospecimen storage duration (10 years)" },
        { id: "RULE-PROT-247-9.1", state: "NEW", title: "Optional withdrawal-from-biospecimen-storage right" },
        { id: "RULE-PROT-247-6.2", state: "MODIFIED", title: "Risks section updated (venipuncture)" },
        { id: "RULE-PROT-247-5.4", state: "MODIFIED", title: "Study procedures section updated (visit schedule)" },
    ],
};

// ---------- EXECUTE screen ----------

export const ICF_EXCERPT = {
    title: "INFORMED CONSENT FORM — ONCO-247",
    study_title:
        "A Phase II Study of Meridian-247 in Patients with Advanced Non-Small Cell Lung Cancer",
    version: "3.1 — Approved 2025-11-14",
    sections: [
        {
            heading: "1. What is the purpose of this study?",
            body:
                "You are being asked to take part in a research study of a study drug called Meridian-247. The purpose of this research is to test whether Meridian-247 is safe and effective in patients with advanced non-small cell lung cancer.",
        },
        {
            heading: "2. What will happen in this study?",
            body:
                "If you agree to participate, you will visit the study site approximately every 3 weeks for up to 12 months. At each visit, you will have a physical exam, blood tests, and imaging scans. The study drug will be given as an infusion at each visit.",
        },
        {
            heading: "3. What are the risks?",
            body:
                "The most common side effects of Meridian-247 include fatigue, nausea, low white blood cell counts, and rash. There is also a risk of allergic reaction to the study drug.",
        },
        {
            heading: "4. What are the benefits?",
            body:
                "You may or may not receive direct medical benefit from participating in this study.",
        },
    ],
    truncated_note: "content truncated for demo — sections 5–14 collapsed",
};

// Rule status list (Middle column, Execute Tab A).
// 24 rules — the 12 named plus 12 additional standard, per spec.
export const RULE_STATUS = [
    { id: "RULE-050.25-A-1", citation: "21 CFR 50.25(a)(1)", title: "Research statement", status: "Satisfied" },
    { id: "RULE-050.25-A-2", citation: "21 CFR 50.25(a)(2)", title: "Foreseeable risks", status: "Gap" },
    { id: "RULE-050.25-A-3", citation: "21 CFR 50.25(a)(3)", title: "Benefits", status: "Satisfied" },
    { id: "RULE-050.25-A-4", citation: "21 CFR 50.25(a)(4)", title: "Alternative procedures", status: "Gap" },
    { id: "RULE-050.25-A-5", citation: "21 CFR 50.25(a)(5)", title: "Confidentiality", status: "Satisfied" },
    { id: "RULE-050.25-A-6", citation: "21 CFR 50.25(a)(6)", title: "Injury compensation", status: "Satisfied" },
    { id: "RULE-050.25-A-7", citation: "21 CFR 50.25(a)(7)", title: "Contacts", status: "Satisfied" },
    { id: "RULE-050.25-A-8", citation: "21 CFR 50.25(a)(8)", title: "Voluntary participation", status: "Satisfied" },
    { id: "RULE-050.25-B-1", citation: "21 CFR 50.25(b)(1)", title: "Unforeseeable risks", status: "Satisfied" },
    { id: "RULE-050.25-B-2", citation: "21 CFR 50.25(b)(2)", title: "Circumstances of termination", status: "Satisfied" },
    { id: "RULE-050.25-B-3", citation: "21 CFR 50.25(b)(3)", title: "Additional costs", status: "Satisfied" },
    { id: "RULE-050.25-B-4", citation: "21 CFR 50.25(b)(4)", title: "Consequences of withdrawal", status: "Satisfied" },
    { id: "RULE-050.25-B-5", citation: "21 CFR 50.25(b)(5)", title: "Biospecimen future use", status: "Critical Gap" },
    { id: "RULE-050.25-B-6", citation: "21 CFR 50.25(b)(6)", title: "New findings", status: "Satisfied" },
    { id: "RULE-050.25-B-7", citation: "21 CFR 50.25(b)(7)", title: "Approx. # of subjects", status: "Satisfied" },
    { id: "RULE-050.25-C", citation: "21 CFR 50.25(c)", title: "Applicable clinical trial registration", status: "Satisfied" },
    { id: "RULE-ICH-4.8.6", citation: "ICH-GCP §4.8.6", title: "Language subject understands", status: "Satisfied" },
    { id: "RULE-ICH-4.8.7", citation: "ICH-GCP §4.8.7", title: "No coercion or undue influence", status: "Satisfied" },
    { id: "RULE-ICH-4.8.8", citation: "ICH-GCP §4.8.8", title: "Ample time to consider", status: "Satisfied" },
    { id: "RULE-ICH-4.8.9", citation: "ICH-GCP §4.8.9", title: "Legally-authorized representative", status: "Satisfied" },
    { id: "RULE-ICH-4.8.10", citation: "ICH-GCP §4.8.10", title: "Revise on new information", status: "Satisfied" },
    { id: "RULE-PROT-247-4.7", citation: "Protocol v2.2 §4.7", title: "Biomarker sub-study procedures", status: "Critical Gap" },
    { id: "RULE-PROT-247-6.2", citation: "Protocol v2.2 §6.2", title: "Venipuncture risks", status: "Gap" },
    { id: "RULE-MER-CLIN-014", citation: "Sponsor SOP MER-CLIN-014", title: "Readability target (8th grade)", status: "Advisory" },
];

export const FINDINGS = [
    {
        id: "F1",
        severity: "Critical Gap",
        rule: "RULE-PROT-247-4.7",
        title: "Biomarker sub-study procedures not disclosed",
        description:
            "The current ICF Section 2 (\"What will happen in this study?\") does not describe the pharmacogenomic biomarker sub-study procedures introduced in Protocol Amendment v2.2, §4.7. Specifically, the additional 10 mL peripheral blood draw at visits 3, 5, and 7 is not disclosed.",
        recommended_action:
            "Add a new paragraph to Section 2 describing the biomarker sub-study procedures.",
        citation: "Protocol Amendment v2.2 §4.7 + 21 CFR 50.25(a)(1)",
    },
    {
        id: "F2",
        severity: "Critical Gap",
        rule: "RULE-050.25-B-5",
        title: "No statement on future biospecimen use",
        description:
            "The current ICF does not contain any statement regarding future research use of biospecimens. Amendment v2.2 introduces optional biospecimen storage for future pharmacogenomic research (Protocol §8.3), triggering 21 CFR 50.25(b)(5).",
        recommended_action:
            "Add a new section covering future biospecimen use, including opt-in/opt-out mechanism and 10-year retention period.",
        citation: "21 CFR 50.25(b)(5) + Protocol Amendment v2.2 §8.3",
    },
    {
        id: "F3",
        severity: "Gap",
        rule: "RULE-PROT-247-6.2",
        title: "Venipuncture-related risks missing",
        description:
            "The current ICF Section 3 (\"What are the risks?\") does not include venipuncture-related risks (bruising, hematoma, infection) introduced by the additional blood draws in Amendment v2.2.",
        recommended_action:
            "Extend Section 3 with a paragraph on additional venipuncture risks.",
        citation: "21 CFR 50.25(a)(2) + Protocol Amendment v2.2 §6.2",
    },
    {
        id: "F4",
        severity: "Gap",
        rule: "RULE-050.25-A-4",
        title: "Alternative procedures not clearly disclosed",
        description:
            "The current ICF does not clearly disclose appropriate alternative procedures or courses of treatment. Section 4 mentions potential benefit but does not describe alternatives (e.g., standard-of-care chemotherapy, palliative care).",
        recommended_action: "Add explicit alternatives section.",
        citation: "21 CFR 50.25(a)(4)",
        historical_note:
            "This is one of the most frequently cited ICF deficiencies in FDA BIMO inspections.",
    },
    {
        id: "F5",
        severity: "Advisory",
        rule: "RULE-MER-CLIN-014",
        title: "Readability above sponsor target",
        description:
            "The current ICF has an estimated Flesch-Kincaid grade level of 11.4. Sponsor SOP MER-CLIN-014 targets 8th grade reading level.",
        recommended_action: "Simplify language in Sections 1, 3, and 4.",
        citation: "Sponsor SOP MER-CLIN-014",
    },
    {
        id: "F6",
        severity: "Satisfied",
        rule: "RULE-050.25-A-8",
        title: "Voluntary participation clearly stated",
        description:
            "Voluntary participation and right to withdraw are clearly stated in the current ICF Section 12. No change required.",
    },
];

export const FINDINGS_SUMMARY = {
    critical: 2,
    gap: 2,
    advisory: 1,
    satisfied: 19,
};

export const PARTICIPANT_SUMMARY = {
    total: 87,
    full_reconsent: 62,
    addendum: 19,
    notification: 6,
    not_applicable: 0,
};

// First 15 rows verbatim from spec, then procedurally generate 2–6.
const SEED_ROWS = [
    { id: "P-0001", site: "Site 01", icf: "v3.1", signed: "2025-11-22", decision: "Full re-consent", reasoning: "Enrolled on active protocol; will undergo visits 3, 5, 7 under amendment", method: "New signature", timing: "Next visit (2026-04-18)" },
    { id: "P-0002", site: "Site 01", icf: "v3.1", signed: "2025-11-23", decision: "Full re-consent", reasoning: "Same as P-0001; upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-19)" },
    { id: "P-0003", site: "Site 01", icf: "v3.0", signed: "2025-10-08", decision: "Full re-consent", reasoning: "Signed on prior ICF version; two version increments behind", method: "New signature", timing: "Next visit (2026-04-21)" },
    { id: "P-0004", site: "Site 02", icf: "v3.1", signed: "2025-12-01", decision: "Addendum", reasoning: "Completed visits 3, 5, 7; only affected by biospecimen storage disclosure", method: "Signed addendum", timing: "Next visit (2026-04-15)" },
    { id: "P-0005", site: "Site 02", icf: "v3.1", signed: "2025-12-02", decision: "Full re-consent", reasoning: "Visit 3 pending; biomarker draw applies", method: "New signature", timing: "Next visit (2026-04-16)" },
    { id: "P-0006", site: "Site 03", icf: "v3.1", signed: "2025-12-05", decision: "Notification only", reasoning: "Off treatment as of 2026-03-01; long-term follow-up only", method: "Letter", timing: "Within 30 days" },
    { id: "P-0007", site: "Site 03", icf: "v3.1", signed: "2025-12-06", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-20)" },
    { id: "P-0008", site: "Site 03", icf: "v3.1", signed: "2025-12-08", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-22)" },
    { id: "P-0009", site: "Site 04", icf: "v3.1", signed: "2025-12-10", decision: "Addendum", reasoning: "Completed all biomarker-relevant visits; biospecimen disclosure applies", method: "Signed addendum", timing: "Next visit (2026-04-17)" },
    { id: "P-0010", site: "Site 04", icf: "v3.1", signed: "2025-12-12", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-19)" },
    { id: "P-0011", site: "Site 05", icf: "v3.0", signed: "2025-10-15", decision: "Full re-consent", reasoning: "Signed on prior ICF version; upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-18)" },
    { id: "P-0012", site: "Site 05", icf: "v3.1", signed: "2025-12-15", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-20)" },
    { id: "P-0013", site: "Site 06", icf: "v3.1", signed: "2025-12-18", decision: "Notification only", reasoning: "Withdrew consent 2026-02-14", method: "No action", timing: "N/A" },
    { id: "P-0014", site: "Site 06", icf: "v3.1", signed: "2025-12-20", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-21)" },
    { id: "P-0015", site: "Site 07", icf: "v3.1", signed: "2026-01-05", decision: "Full re-consent", reasoning: "Upcoming biomarker visits", method: "New signature", timing: "Next visit (2026-04-22)" },
];

// Procedural pages 2–6 — realistic distribution
const generateRest = () => {
    const rows = [];
    let idNum = 16;
    // 5 more pages = 75 more rows
    const sites = ["Site 01", "Site 02", "Site 03", "Site 04", "Site 05", "Site 06", "Site 07", "Site 08", "Site 09", "Site 10", "Site 11", "Site 12"];
    const decisionMix = [];
    // Match summary: still need ~50 full re-consent, ~17 addendum, ~5 notification, mixed
    for (let i = 0; i < 50; i++) decisionMix.push("Full re-consent");
    for (let i = 0; i < 17; i++) decisionMix.push("Addendum");
    for (let i = 0; i < 5; i++) decisionMix.push("Notification only");
    for (let i = 0; i < 3; i++) decisionMix.push("Full re-consent");
    // Shuffle deterministically
    for (let i = decisionMix.length - 1; i > 0; i--) {
        const j = (i * 7 + 13) % (i + 1);
        [decisionMix[i], decisionMix[j]] = [decisionMix[j], decisionMix[i]];
    }
    const signedBase = new Date("2025-11-15");
    const nextBase = new Date("2026-04-14");
    for (let k = 0; k < 75; k++) {
        const num = idNum + k;
        const id = `P-${String(num).padStart(4, "0")}`;
        const siteIdx = (k * 3 + num) % sites.length;
        const site = sites[siteIdx];
        const decision = decisionMix[k];
        const icf = (num % 11 === 0) ? "v3.0" : "v3.1";
        const signedD = new Date(signedBase.getTime() + ((num * 7) % 55) * 86400000);
        const signed = signedD.toISOString().slice(0, 10);
        const nextD = new Date(nextBase.getTime() + ((num * 3) % 12) * 86400000);
        const next = nextD.toISOString().slice(0, 10);
        let reasoning, method, timing;
        if (decision === "Full re-consent") {
            reasoning = icf === "v3.0"
                ? "Signed on prior ICF version; upcoming biomarker visits"
                : "Upcoming biomarker visits under amendment";
            method = "New signature";
            timing = `Next visit (${next})`;
        } else if (decision === "Addendum") {
            reasoning = "Completed biomarker-relevant visits; biospecimen disclosure applies";
            method = "Signed addendum";
            timing = `Next visit (${next})`;
        } else {
            reasoning = num % 2 === 0 ? "Off treatment; long-term follow-up only" : "Withdrew consent 2026-02-14";
            method = num % 2 === 0 ? "Letter" : "No action";
            timing = num % 2 === 0 ? "Within 30 days" : "N/A";
        }
        rows.push({ id, site, icf, signed, decision, reasoning, method, timing });
    }
    return rows;
};

export const PARTICIPANTS = [...SEED_ROWS, ...generateRest()];

export const P0003_DETAIL = {
    id: "P-0003",
    site: "Site 01",
    consent_history: [
        { version: "v3.0", signed: "2025-10-08", note: "initial enrollment" },
        {
            version: "v3.1",
            signed: null,
            note: "IRB-approved 2025-11-14 — participant NOT re-consented (protocol deviation flag)",
        },
    ],
    visit_history: [
        { visit: "V1 · Screening", date: "2025-10-01" },
        { visit: "V2 · Baseline", date: "2025-10-08" },
        { visit: "V3", date: "2025-10-29", note: "no biomarker draw — pre-amendment" },
    ],
    rule_triggers: [
        {
            id: "RULE-PROT-247-4.7",
            reason: "Biomarker sub-study — participant will undergo affected V5, V7",
        },
        { id: "RULE-050.25-B-5", reason: "Biospecimen future use — new element" },
        { id: "RULE-PROT-247-6.2", reason: "Venipuncture risks — new risk" },
    ],
    additional_flag:
        "Prior version compliance gap — participant should have been re-consented to v3.1 in Nov 2025. Recommend documenting deviation and re-consenting directly to v3.2.",
};

// ---------- EXPLAIN screen ----------

export const P0118_EVIDENCE = {
    id: "P-0118",
    site: "Site 08",
    sponsor: "Meridian Oncology · ONCO-247",
    blocks: [
        {
            id: "b1",
            title: "Consent Event",
            rows: [
                { k: "Event", v: "Initial informed consent signed" },
                { k: "Date/time", v: "2025-12-14 09:42 EST" },
                { k: "Participant signature", v: "Verified (Florence eConsent audit trail)", ok: true },
                {
                    k: "Investigator signature",
                    v: "Dr. Rachel Nguyen, MD (PI) — Verified",
                    ok: true,
                },
                {
                    k: "Witness signature",
                    v: "J. Ortiz, RN — Verified (impartial witness per site SOP)",
                    ok: true,
                },
                { k: "ICF version signed", v: "v3.1" },
                {
                    k: "Source system",
                    v: "Florence eBinders (site) · Veeva Vault eTMF (sponsor)",
                },
            ],
        },
        {
            id: "b2",
            title: "IRB Approval Status at Time of Signing",
            rows: [
                { k: "IRB", v: "Advarra Central IRB" },
                { k: "Approval letter", v: "ADV-2025-08841 dated 2025-11-14" },
                { k: "Approved ICF version", v: "v3.1" },
                {
                    k: "Approval status at 2025-12-14",
                    v: "Active (window 2025-11-14 → 2026-11-13)",
                    ok: true,
                },
                { k: "Continuing review", v: "Not yet due" },
                { k: "Citation", v: "21 CFR 56.109(a); ICH-GCP E6(R3) §3.2" },
            ],
        },
        {
            id: "b3",
            title: "Regulatory Element Compliance · 21 CFR 50.25(a)",
            checklist: [
                { c: "50.25(a)(1)", label: "Research statement, purpose, duration, procedures", status: "ok", where: "Section 1–2 of ICF" },
                { c: "50.25(a)(2)", label: "Reasonably foreseeable risks", status: "ok", where: "Section 3" },
                { c: "50.25(a)(3)", label: "Benefits", status: "ok", where: "Section 4" },
                { c: "50.25(a)(4)", label: "Alternative procedures", status: "partial", where: "Section 4 · flagged for v3.2 remediation" },
                { c: "50.25(a)(5)", label: "Confidentiality + FDA inspection notice", status: "ok", where: "Section 8" },
                { c: "50.25(a)(6)", label: "Compensation for injury", status: "ok", where: "Section 9" },
                { c: "50.25(a)(7)", label: "Contacts", status: "ok", where: "Section 10" },
                { c: "50.25(a)(8)", label: "Voluntary participation + withdrawal", status: "ok", where: "Section 12" },
            ],
        },
        {
            id: "b4",
            title: "Recruitment Material Consistency",
            rows: [
                { k: "Recruitment flyer version", v: "RM-247-v2 · approved 2025-11-14" },
                { k: "Consistency with ICF v3.1", v: "Verified", ok: true },
                {
                    k: "Citation",
                    v: "FDA guidance — recruitment materials considered part of the informed consent process",
                },
            ],
        },
        {
            id: "b5",
            title: "Readability",
            rows: [
                { k: "Flesch-Kincaid grade level", v: "9.2 (measured on v3.1 at time of signing)" },
                { k: "Sponsor target", v: "8th grade" },
                {
                    k: "Status",
                    v: "Slightly above target · advisory, not a regulatory violation",
                    warn: true,
                },
            ],
        },
        {
            id: "b6",
            title: "Amendment v2.2 Re-Consent Status",
            rows: [
                {
                    k: "Amendment introduces",
                    v: "Biomarker sub-study · biospecimen future use · additional venipuncture risks",
                },
                {
                    k: "Participant's affected visits",
                    v: "V5 (scheduled 2026-05-08), V7 (scheduled 2026-06-19)",
                },
                { k: "Re-consent decision", v: "Full re-consent required (v3.2)" },
                { k: "Recommended timing", v: "Before V5 — no later than 2026-05-01" },
                {
                    k: "Current status",
                    v: "Pending — draft ICF v3.2 awaiting IRB approval",
                    warn: true,
                },
            ],
        },
        {
            id: "b7",
            title: "Chain of Evidence Summary",
            timeline: [
                { d: "2025-10-01", e: "Screening visit" },
                { d: "2025-11-14", e: "IRB approves ICF v3.1" },
                { d: "2025-12-14", e: "Participant signs ICF v3.1" },
                { d: "2026-01-20", e: "V1 · baseline" },
                { d: "2026-02-11", e: "V2" },
                { d: "2026-03-04", e: "V3" },
                { d: "2026-04-15", e: "Amendment v2.2 goes live · re-consent required before V5", flag: true },
                { d: "2026-05-08", e: "V5 · upcoming, re-consent gate", flag: true },
            ],
        },
    ],
};
